interface CacheStats {
  created: number;
  invalidated: number;
  caches: string[];
}

const THRESHOLD_ITEMS = 100_000;
const IS_CACHE_RECORDING_ENABLED = true;

export class ManifestServiceStats {
  #memory: Record<
    string,
    Record<
      string,
      {
        value: number;
        time: number;
      }[]
    >
  > = {};
  #caches: Record<string, CacheStats> = {};

  #isRecording = false;
  #startedAt = 0;
  #stoppedAt = 0;

  #timeline: {
    caches: {
      namespace: string;
      cacheKey: string;
      event: 'created' | 'invalidated';
      reason: string | null;
      time: number;
    }[];
    methods: {
      method: string;
      cacheHit: boolean;
      time: number;
      executeTime: number;
    }[];
  } = {
    methods: [],
    caches: [],
  };

  startRecording() {
    if (this.#isRecording) {
      return;
    }

    this.#timeline = {
      caches: [],
      methods: [],
    };

    this.#caches = {};
    this.#memory = {};

    this.#isRecording = true;

    this.#startedAt = performance.now();
    this.#stoppedAt = 0;

    console.log('platform stats recording started');
  }

  stopRecording() {
    this.#isRecording = false;
    this.#stoppedAt = performance.now();
    console.log('platform stats recording started');
  }

  reset() {
    this.#caches = {};

    this.#startedAt = 0;
  }

  memory(namespace: string, cacheKey: string, value: number) {
    if (!this.#isRecording) {
      return;
    }

    if (!this.#memory[namespace]) {
      this.#memory[namespace] = {};
    }

    if (!this.#memory[namespace][cacheKey]) {
      this.#memory[namespace][cacheKey] = [];
    }

    this.#memory[namespace][cacheKey].push({
      value,
      time: performance.now() - this.#startedAt,
    });
  }

  method(method: string, hit: boolean, executeTime: number) {
    if (!this.#isRecording) {
      return;
    }

    this.#timeline.methods.push({
      method,
      cacheHit: hit,
      time: performance.now() - this.#startedAt,
      executeTime,
    });

    if (this.#timeline.methods.length > THRESHOLD_ITEMS) {
      this.stopRecording();
    }
  }

  cache(
    namespace: string,
    cacheKey: string,
    event: 'created' | 'invalidated',
    value: unknown,
    reason?: string,
  ) {
    if (IS_CACHE_RECORDING_ENABLED || !this.#isRecording) {
      return;
    }

    if (!this.#caches[namespace]) {
      this.#caches[namespace] = {
        created: 0,
        invalidated: 0,
        caches: [],
      };
    }

    if (event === 'created') {
      this.#caches[namespace].created++;
    } else if (event === 'invalidated') {
      this.#caches[namespace].invalidated++;
    }

    this.#caches[namespace].caches.push(cacheKey);

    this.#timeline.caches.push({
      namespace,
      cacheKey,
      event,
      reason: event === 'invalidated' ? reason ?? 'unknown reason' : null,
      time: performance.now() - this.#startedAt,
    });

    try {
      const size = value === null ? 0 : JSON.stringify(value).length;

      if (event === 'created') {
        this.memory(namespace, cacheKey, size);
      } else if (event === 'invalidated') {
        this.memory(namespace, cacheKey, size * -1);
      }
    } catch (e) {}

    if (this.#timeline.caches.length > THRESHOLD_ITEMS) {
      this.stopRecording();
    }
  }

  getStats() {
    this.stopRecording();
    return {
      duration: this.#stoppedAt - this.#startedAt,
      timeline: this.#timeline,
      caches: this.#caches,
      memory: this.#memory,
    };
  }
}
